import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { MultiSelect } from 'controlled-components'
import {
  closeBottom,
  selectBottomIsOpen,
  setBottomSearchTerm,
  setBottomShowingSelections,
} from 'slices/aiSegmentBuilder/picker'

import {
  CaptionContainer,
  MenuContainer,
  TriggerContainer,
} from './BottomSelector'
import { usePickerDataFetching } from './usePickerDataFetching'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface BottomSelectorContainerProps {
  pickerId?: number
  disabled?: boolean
  placeholder?: string
}

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for the bottom (multi-select) dropdown.
 * Accepts pickerId and disabled as props (injected by PickerContainer via cloneElement).
 * Passes pickerId and disabled as props to all sub-containers.
 */
const BottomSelectorContainer: React.FC<BottomSelectorContainerProps> = ({
  pickerId = 0,
  disabled = false,
  placeholder,
}) => {
  const dispatch = useDispatch()

  // Read from Redux using the resolved pickerId
  const isOpen = useSelector((state: any) =>
    selectBottomIsOpen(state, pickerId)
  )

  // Single source of truth for data fetching - all values passed to children as props
  const { search, refetch, goToPage, isLoading } = usePickerDataFetching({
    pickerId,
    level: 'bottom',
    debounceMs: 300,
  })

  // Handle close: reset search term and refetch unfiltered data
  const handleClose = useCallback(() => {
    dispatch(closeBottom({ pickerId }))
    dispatch(setBottomSearchTerm({ pickerId, term: '' }))
    dispatch(setBottomShowingSelections({ pickerId, showingSelections: false }))
    search('')
  }, [dispatch, pickerId, search])

  return (
    <MultiSelect isOpen={isOpen} onClose={handleClose}>
      {/* Wrapper to position menu relative to trigger */}
      <div style={{ position: 'relative' }}>
        <TriggerContainer
          pickerId={pickerId}
          placeholder={placeholder}
          disabled={disabled}
          search={search}
          refetch={refetch}
          isLoading={isLoading}
        />
        <MenuContainer
          pickerId={pickerId}
          disabled={disabled}
          isLoading={isLoading}
          goToPage={goToPage}
        />
      </div>
      <CaptionContainer pickerId={pickerId} disabled={disabled} />
    </MultiSelect>
  )
}

// Add displayName for identification in PickerContainer filtering
BottomSelectorContainer.displayName = 'BottomSelectorContainer'

export default BottomSelectorContainer
